import {
    ArrowDown,
    Box,
    Caption,
    Chip,
    DropdownContent,
    DropdownMenu,
    DropdownTrigger,
    SearchBar,
    TextBlock,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import workflowStyle from "./all-workflows-table.scss";
import SearchView from "components/search/SearchView";
import { AUTH_USER_VIEW } from "domain/authority";
import { featureFlagService, FLAG_UICOMPS_WORKFLOWS_TABLE } from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { DefaultWorkflowUser } from "services/workflows/WorkflowService";
import formStyle from "styles/form.scss";
import { findScrollableAncestor, useCloseDropdownOnScroll } from "utils/useCloseDropdownOnScroll";
import { useOutsideClick } from "utils/useOutsideClick";

import testIds from "testIds.json";

interface Props {
    status?: React.RefObject<HTMLDivElement>;
    userDetails: DefaultWorkflowUser[];
    own: boolean;
    userCount: number;
}

export const ActiveWorkflowUserList = (props: Props) => {
    const { t } = useTranslation();
    const [searchItem, setSearchItem] = React.useState("");
    const [isDropdownOpen, setDropdownOpen] = React.useState(false);
    // TODO BCC-8002: Remove this and all redundant code and styling after Workflows table rewrite is complete
    const isWorkflowsTableFeatureEnabled = featureFlagService.isFeatureEnabled(FLAG_UICOMPS_WORKFLOWS_TABLE);

    const handleClickOutside = () => {
        props.status?.current?.click();
    };
    const ref = isWorkflowsTableFeatureEnabled
        ? useOutsideClick(handleClickOutside)
        : React.useRef<HTMLDivElement>(null);

    useCloseDropdownOnScroll(isDropdownOpen, findScrollableAncestor(ref.current));

    const listItems = props.userDetails.map((item, index) => {
        if (!isWorkflowsTableFeatureEnabled) {
            return (
                <ul key={index}>
                    <div className={workflowStyle.listItem}>{item.name === null ? item.username : item.name}</div>
                </ul>
            );
        }

        return <Box key={index}>{item.name === null ? item.username : item.name}</Box>;
    });

    const searchResult = props.userDetails.filter((item) => {
        const query = searchItem.toLowerCase();
        const displayValue = item.name ?? item.username;
        return displayValue.toLowerCase().includes(query);
    });

    const summaryText = (
        <>
            {!props.own ? <TextBlock disableBottomSpacing>{t("WorkflowsTable.defaultFor")}</TextBlock> : " + "}
            <TextBlock disableBottomSpacing>{t("WorkflowsTable.users", { count: props.userCount })}</TextBlock>
        </>
    );

    if (!isWorkflowsTableFeatureEnabled) {
        return (
            <div
                ref={ref}
                className={workflowStyle.searchContainer}
                data-testid={testIds.workArea.workflows.table.defaultList.itself}
            >
                <div className={classNames(formStyle.search, workflowStyle.searchBox)}>
                    <SearchView setSearch={setSearchItem} searchInProgress={false} realTimeSearch={true} />
                </div>
                <div className={workflowStyle.itemContainer}>
                    {searchItem === "" ? (
                        listItems
                    ) : searchResult.length === 0 ? (
                        <div className={workflowStyle.noResultBox}>{t("WorkflowsTable.searchEmptyStateMessage")}</div>
                    ) : (
                        searchResult.map((item, index) => (
                            <ul key={index}>
                                <div className={workflowStyle.listItem}>{item.name || item.username}</div>
                            </ul>
                        ))
                    )}
                </div>
            </div>
        );
    }

    if (isWorkflowsTableFeatureEnabled && !userSessionService.userHasAllAuthorities([AUTH_USER_VIEW])) {
        return (
            <Box verticalAlignment="Center" gap="Xxxs">
                {summaryText}
            </Box>
        );
    }

    return (
        <Box onClick={(event) => event.stopPropagation()} ref={ref}>
            <DropdownMenu
                closeOnSelection
                dropdownOffset="Xxs"
                onDropdownOpenChange={(isOpen: boolean) => setDropdownOpen(isOpen)}
                position="bottom-start"
                flipOptions={{
                    boundary: findScrollableAncestor(ref.current),
                    fallbackPlacements: ["bottom-end", "top-start", "top-end"],
                }}
            >
                <DropdownTrigger>
                    <Chip testId={testIds.workArea.workflows.table.defaultList.button} aria-expanded={isDropdownOpen}>
                        <Box
                            verticalAlignment="Center"
                            gap="Xxxs"
                            onClick={() => {
                                usageStatisticsService.sendEvent({
                                    category: Category.WORKFLOW,
                                    action: Action.SHOW_USER_LIST_IN_WORKFLOW,
                                });
                            }}
                        >
                            {summaryText}
                            <ArrowDown
                                className={classNames(workflowStyle.toggleArrow, workflowStyle.down, {
                                    [workflowStyle.up]: isDropdownOpen,
                                })}
                            />
                        </Box>
                    </Chip>
                </DropdownTrigger>
                <DropdownContent testId={testIds.workArea.workflows.table.defaultList.itself} maxHeight={125}>
                    <Box columnLayout>
                        <SearchBar
                            name="userListSearchBar"
                            placeholder={t("Common.search")}
                            searchOnChange
                            onSearch={(value) => setSearchItem(value.trim())}
                            onClear={() => setSearchItem("")}
                            width={220}
                            className={workflowStyle.searchBar}
                        />

                        <Box padding="Xxs" gap="Xxs" columnLayout>
                            {searchItem === "" ? (
                                listItems
                            ) : searchResult.length === 0 ? (
                                <Caption greyTextStyle>{t("WorkflowsTable.searchEmptyStateMessage")}</Caption>
                            ) : (
                                searchResult.map((item, index) => <Box key={index}>{item.name || item.username}</Box>)
                            )}
                        </Box>
                    </Box>
                </DropdownContent>
            </DropdownMenu>
        </Box>
    );
};
